import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import type {
  AgreementCategory,
  AgreementEntityType,
  AgreementLanguage,
  AgreementStatus,
  AgreementType,
  BillingCycle,
  CollectionPolicy,
  DemandChargeAllocationMethod,
  EntityCountLevel,
  LineItemGrouping,
  PaymentOutcome,
  PaymentTerminalStatus,
  PricingModel,
  ProductEntityType,
  ProductPricingType,
  ProductStatus,
  ProductType,
  ProductVisibility,
  ReimbursementStatus,
  TierQuantitySource,
  UsagePricingType,
  VolumeTiersMode
} from '~/data/types/billing'

/** Billing cycle for display (API billing cycles plus the legacy ANNUALLY value still shown in filters). */
export type BillingCycleDisplay = BillingCycle | 'ANNUALLY'

/** Category for display. */
export type AgreementCategoryDisplay = AgreementCategory

/**
 * Hook that returns a memoized formatter function for product status values.
 *
 * @returns A callback function that takes a ProductStatus and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatProductStatus = useProductStatusFormatter()
 * const displayText = formatProductStatus('ACTIVE') // "Active"
 * ```
 */
export function useProductStatusFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((status: ProductStatus) => t(`products_statuses_${status}`), [t])
}

/**
 * Hook that returns a memoized formatter function for product entity type values.
 *
 * Entity types represent the different kinds of assets that products can be associated with,
 * such as sites, chargers, connectors, vehicles, etc.
 *
 * @returns A callback function that takes a ProductEntityType and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatEntityType = useProductEntityTypeFormatter()
 * const displayText = formatEntityType('CHARGER') // "Charger"
 * ```
 */
export function useProductEntityTypeFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((entityType: ProductEntityType) => t(`products_entityTypes_${entityType}`), [t])
}

/**
 * Hook that returns a memoized formatter function for product type values.
 *
 * Product types categorize billing products by their primary purpose, such as charging access,
 * fleet management, infrastructure services, etc.
 *
 * @returns A callback function that takes a ProductType and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatProductType = useProductTypeFormatter()
 * const displayText = formatProductType('CHARGING_ACCESS') // "Charging Access"
 * ```
 */
export function useProductTypeFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((productType: ProductType) => t(`products_types_${productType}`), [t])
}

/**
 * Hook that returns a memoized formatter function for usage pricing type values.
 *
 * Usage pricing types define how usage-based products are billed, such as per unit
 * consumption or time-of-use rates.
 *
 * @returns A callback function that takes a UsagePricingType and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatUsagePricingType = useUsagePricingTypeFormatter()
 * const displayText = formatUsagePricingType('TIME_OF_USE') // "Time of Use"
 * ```
 */
export function useUsagePricingTypeFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((usagePricingType: UsagePricingType) => t(`products_usagePricingTypes_${usagePricingType}`), [t])
}

/**
 * Hook that returns a memoized formatter function for product pricing type values.
 *
 * Product pricing types indicate how a product is priced, such as flat rate,
 * per unit, or time-of-use billing structures.
 *
 * @returns A callback function that takes a ProductPricingType and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatPricingType = useProductPricingTypeFormatter()
 * const displayText = formatPricingType('FLAT_RATE') // "Flat Rate"
 * ```
 */
export function useProductPricingTypeFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((pricingType: ProductPricingType) => t(`products_pricingTypes_${pricingType}`), [t])
}

/**
 * Hook that returns a memoized formatter function for product visibility values.
 *
 * Product visibility determines how and where products can be accessed, such as
 * private (organization only), marketplace (cross-organization), or public availability.
 *
 * @returns A callback function that takes a ProductVisibility and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatVisibility = useProductVisibilityFormatter()
 * const displayText = formatVisibility('MARKETPLACE') // "Marketplace"
 * ```
 */
export function useProductVisibilityFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((visibility: ProductVisibility) => t(`products_visibility_${visibility}`), [t])
}

/**
 * Hook that returns a memoized formatter function for pricing model values.
 *
 * Pricing models define how a product is priced, such as flat rate,
 * usage-based, or time-of-use billing structures.
 *
 * @returns A callback function that takes a PricingModel and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatPricingModel = usePricingModelFormatter()
 * const displayText = formatPricingModel('FLAT') // "Flat rate"
 * ```
 */
export function usePricingModelFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((pricingModel: PricingModel) => t(`products_pricingModels_${pricingModel}`), [t])
}

export function useVolumeTiersModeFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((mode: VolumeTiersMode) => t(`products_volumeTiersModes_${mode}`), [t])
}

export function useTierQuantitySourceFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((source: TierQuantitySource) => t(`products_tierQuantitySources_${source}`), [t])
}

export function useEntityCountLevelFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((level: EntityCountLevel) => t(`products_entityCountLevels_${level}`), [t])
}

/**
 * Hook that returns a memoized formatter function for agreement entity type values.
 *
 * Agreement entity types represent the different kinds of assets that agreements can be associated with,
 * such as sites, chargers, connectors, vehicles, etc.
 *
 * @returns A callback function that takes an AgreementEntityType and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatEntityType = useAgreementEntityTypeFormatter()
 * const displayText = formatEntityType('USER_GROUP') // "User Groups"
 * ```
 */
export function useAgreementEntityTypeFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((entityType: AgreementEntityType) => t(`agreements_agreementEntityType_${entityType}`), [t])
}

/**
 * Hook that returns a memoized formatter function for billing cycle values.
 *
 * Billing cycles define the frequency and timing of billing periods, such as
 * monthly, quarterly, or custom billing intervals.
 *
 * @returns A callback function that takes a BillingCycle and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatBillingCycle = useBillingCycleFormatter()
 * const displayText = formatBillingCycle('MONTHLY') // "Monthly"
 * ```
 */
export function useBillingCycleFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((billingCycle: BillingCycleDisplay) => t(`billingCycle_${billingCycle}`), [t])
}

/**
 * Hook that returns a memoized formatter function for agreement collection policy values.
 *
 * Collection policies define who, if anyone, collects payment for rated agreement usage.
 *
 * @returns A callback function that takes a CollectionPolicy and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatCollectionPolicy = useCollectionPolicyFormatter()
 * const displayText = formatCollectionPolicy('SYNOP_COLLECTS') // "Platform Collects"
 * ```
 */
export function useCollectionPolicyFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((collectionPolicy: CollectionPolicy) => t(`collectionPolicy_${collectionPolicy}`), [t])
}

/**
 * Hook that returns a memoized formatter function for demand-charge allocation methods.
 *
 * The method defines how a customer's share of a site's utility demand charge is measured
 * at period close: their kW during the site's peak interval, or their own period maximum.
 *
 * @example
 * ```tsx
 * const formatDemandChargeAllocationMethod = useDemandChargeAllocationMethodFormatter()
 * const displayText = formatDemandChargeAllocationMethod('SITE_PEAK_CONTRIBUTION') // "Site Peak Contribution"
 * ```
 */
export function useDemandChargeAllocationMethodFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((method: DemandChargeAllocationMethod) => t(`demandChargeAllocationMethod_${method}`), [t])
}

/**
 * Hook that returns a memoized formatter function for line item grouping values.
 *
 * Line item groupings control how usage line items are rolled up into invoice items
 * on period (non-real-time) invoices.
 *
 * @returns A callback function that takes a LineItemGrouping and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatLineItemGrouping = useLineItemGroupingFormatter()
 * const displayText = formatLineItemGrouping('PRODUCT_SITE') // "By Product & Site"
 * ```
 */
export function useLineItemGroupingFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((grouping: LineItemGrouping) => t(`lineItemGrouping_${grouping}`), [t])
}

/**
 * Hook that returns a memoized formatter function for agreement language values.
 *
 * The agreement language drives the language the agreement's invoices are generated in.
 *
 * @returns A callback function that takes an AgreementLanguage and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatLanguage = useAgreementLanguageFormatter()
 * const displayText = formatLanguage('FRENCH') // "French"
 * ```
 */
export function useAgreementLanguageFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((language: AgreementLanguage) => t(`agreementLanguage_${language}`), [t])
}

/**
 * Hook that returns a memoized formatter function for agreement category values.
 *
 * Agreement categories classify agreements by their primary purpose or type,
 * such as service agreements, product subscriptions, or partnership contracts.
 *
 * @returns A callback function that takes an AgreementCategory and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatCategory = useAgreementCategoryFormatter()
 * const displayText = formatCategory('DRIVER') // "Driver"
 * ```
 */
export function useAgreementCategoryFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((category: AgreementCategoryDisplay) => t(`agreements_agreementCategory_${category}`), [t])
}

/**
 * Hook that returns a memoized formatter function for agreement type values.
 *
 * Agreement types define the specific nature of the agreement, such as
 * master agreements, addendums, amendments, or specific contract types.
 *
 * @returns A callback function that takes an AgreementType and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatType = useAgreementTypeFormatter()
 * const displayText = formatType('MARKETPLACE') // "Marketplace"
 * ```
 */
export function useAgreementTypeFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((type: AgreementType) => t(`agreements_agreementType_${type}`), [t])
}

/**
 * Hook that returns a memoized formatter function for agreement status values.
 *
 * Agreement statuses indicate the current state of an agreement, such as
 * draft, active, suspended, expired, or terminated.
 *
 * @returns A callback function that takes an AgreementStatus and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatStatus = useAgreementStatusFormatter()
 * const displayText = formatStatus('ACTIVE') // "Active"
 * ```
 */
export function useAgreementStatusFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((status: AgreementStatus) => t(`agreements_agreementStatus_${status}`), [t])
}

/**
 * Hook that returns a formatter function for reimbursement status values.
 *
 * @returns A function that takes a ReimbursementStatus and returns the localized status string
 *
 * @example
 * ```tsx
 * const formatStatus = useReimbursementStatusFormatter()
 * const statusLabel = formatStatus('FINALIZED') // Returns "Finalized"
 * ```
 */
export function usePaymentOutcomeFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((outcome: PaymentOutcome) => t(`paymentOutcome_${outcome}`), [t])
}

export function useReimbursementStatusFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((status: ReimbursementStatus) => t(`reimbursements_status_${status}`), [t])
}

/**
 * Hook that returns a memoized formatter function for payment terminal status values.
 *
 * Payment terminal statuses indicate the current state of a payment terminal, such as
 * online or offline.
 *
 * @returns A callback function that takes a PaymentTerminalStatus and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatStatus = usePaymentTerminalStatusFormatter()
 * const displayText = formatStatus('ONLINE') // "Online"
 * ```
 */
export function usePaymentTerminalStatusFormatter() {
  const t = useTranslations('data.billing')
  return useCallback((status: PaymentTerminalStatus) => t(`paymentTerminalStatus_${status}`), [t])
}

/**
 * Hook that returns a memoized formatter function for meter unit values.
 *
 * Meter units indicate the measurement type recorded by a meter (e.g. KILOWATT_HOURS, MILES).
 * The raw enum value comes from the API; this formatter resolves it to a localized,
 * human-readable label. Falls back to the raw value when no translation exists.
 *
 * @returns A callback function that takes a meter unit and returns the localized display string
 *
 * @example
 * ```tsx
 * const formatMeterUnit = useMeterUnitFormatter()
 * const displayText = formatMeterUnit('KILOWATT_HOURS') // "kWh"
 * ```
 */
export function useMeterUnitFormatter() {
  const t = useTranslations('data.billing')
  return useCallback(
    (unit?: string | null) => {
      if (!unit) return ''
      const key = `meterUnit_${unit}` as Parameters<typeof t>[0]
      // Use t.has when available; otherwise call t() and accept the missing-key fallback.
      if (typeof t.has === 'function' && !t.has(key)) {
        return unit
      }
      return t(key)
    },
    [t]
  )
}
